import React, { useEffect, useState } from "react";
import { ShieldOff, Plus, Trash2, Globe } from "lucide-react";
import { apiGet, apiPost, apiPut, apiDelete } from "../../utils/api";
import { toast } from "../../utils/toast";
import AgentReleaseSettings from "./AgentReleaseSettings";
import "./admin-shell.css";

function formatDateForInput(d) {
  if (!d) return "";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "";
  const pad = (num) => String(num).padStart(2, "0");
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const hh = pad(date.getHours());
  const min = pad(date.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
}

export default function BlockedDomains() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);

  const [systemSaving, setSystemSaving] = useState(false);
  const [systemConfig, setSystemConfig] = useState({
    announcementText: "",
    announcementEnabled: false,
    maintenanceStart: "",
    maintenanceEnd: "",
    maintenanceEnabled: false,
    maintenanceComment: "",
    restrictDownloadToGcaBrowser: true,
    safeBrowsingSafeTtlDays: 60,
    safeBrowsingMaliciousTtlDays: 60,
  });

  async function load() {
    setLoading(true);
    try {
      const res = await apiGet("/api/system-config/blocked-domains");
      const data = await res.json().catch(() => ({}));
      if (res.ok) setDomains(data.domains || []);
      else toast.error(data.message || "Failed to load blocked domains");
    } catch {
      toast.error("Network error loading blocked domains");
    } finally {
      setLoading(false);
    }
  }

  const fetchSystemConfig = async () => {
    try {
      const res = await apiGet("/api/system-config");
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.config) {
          const cfg = data.config;
          setSystemConfig({
            announcementText: cfg.announcementText || "",
            announcementEnabled: !!cfg.announcementEnabled,
            maintenanceStart: formatDateForInput(cfg.maintenanceStart),
            maintenanceEnd: formatDateForInput(cfg.maintenanceEnd),
            maintenanceEnabled: !!cfg.maintenanceEnabled,
            maintenanceComment: cfg.maintenanceComment || "",
            restrictDownloadToGcaBrowser: cfg.restrictDownloadToGcaBrowser !== false,
            safeBrowsingSafeTtlDays: cfg.safeBrowsingSafeTtlDays || 60,
            safeBrowsingMaliciousTtlDays: cfg.safeBrowsingMaliciousTtlDays || 60,
          });
        }
      }
    } catch {
      toast.error("Failed to load system configuration.");
    }
  };

  const handleSaveSystemConfig = async (e) => {
    e.preventDefault();
    setSystemSaving(true);
    try {
      const res = await apiPut("/api/system-config", systemConfig);
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success("System configuration saved successfully.");
        await fetchSystemConfig();
      } else {
        toast.error(data.message || "Failed to save configuration.");
      }
    } catch {
      toast.error("Network error while saving configuration.");
    } finally {
      setSystemSaving(false);
    }
  };

  useEffect(() => {
    void load();
    void fetchSystemConfig();
  }, []);

  async function handleAdd() {
    const domain = input.trim().toLowerCase();
    if (!domain) return;
    setSaving(true);
    try {
      const res = await apiPost("/api/system-config/blocked-domains", { domain });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setDomains(data.domains || []);
        setInput("");
        toast.success(`${domain} blocked`);
      } else {
        toast.error(data.message || "Failed to add domain");
      }
    } catch {
      toast.error("Network error adding domain");
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(domain) {
    if (!window.confirm(`Remove ${domain} from the blocked list?`)) return;
    try {
      const res = await apiDelete(`/api/system-config/blocked-domains/${encodeURIComponent(domain)}`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setDomains(data.domains || []);
        toast.success(`${domain} unblocked`);
      } else {
        toast.error(data.message || "Failed to remove domain");
      }
    } catch {
      toast.error("Network error removing domain");
    }
  }

  return (
    <div className="admin-workspace">
      <div className="admin-shell-header">
        <div className="admin-shell-header__left">
          <h1 className="admin-shell-title">Platform Settings</h1>
          <p className="admin-shell-sub">
            Global portal configuration — announcements, maintenance windows, download page access, and blocked email domains.
          </p>
        </div>
      </div>

      {/* ════════════════════════════
          System Settings
          ════════════════════════════ */}
      <div className="admin-panel" style={{ marginTop: "20px" }}>
        <div className="admin-panel-head">
          <div>
            <h2 className="admin-panel-title">System Settings</h2>
            <p className="admin-panel-note">Configure global portal parameters, landing page announcement banners, and scheduled maintenance breaks.</p>
          </div>
        </div>

        <form onSubmit={handleSaveSystemConfig} className="admin-stack-md" style={{ maxWidth: "600px", marginTop: "16px" }}>
          {/* Announcement Section */}
          <div style={{ borderBottom: "1px solid var(--as-border)", paddingBottom: "20px", marginBottom: "20px" }}>
            <h3 style={{ fontSize: "0.95rem", fontWeight: 600, marginBottom: "12px", color: "var(--as-text)" }}>Landing Page Announcement Marquee</h3>

            <div className="admin-form-field" style={{ marginBottom: "12px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", textTransform: "none", fontWeight: "normal", fontSize: "0.875rem", color: "var(--as-text)" }}>
                <input
                  type="checkbox"
                  checked={systemConfig.announcementEnabled}
                  onChange={e => setSystemConfig(prev => ({ ...prev, announcementEnabled: e.target.checked }))}
                  style={{ width: "16px", height: "16px" }}
                />
                Enable Announcement Marquee
              </label>
            </div>

            <div className="admin-form-field">
              <label htmlFor="announcementText" style={{ fontSize: "0.78rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--as-text-2)", marginBottom: "4px", display: "block" }}>Announcement Text</label>
              <textarea
                id="announcementText"
                className="admin-textarea"
                placeholder="Enter discount offers or announcement messages..."
                value={systemConfig.announcementText}
                onChange={e => setSystemConfig(prev => ({ ...prev, announcementText: e.target.value }))}
              />
            </div>
          </div>

          {/* Maintenance Section */}
          <div>
            <h3 style={{ fontSize: "0.95rem", fontWeight: 600, marginBottom: "12px", color: "var(--as-text)" }}>Scheduled Maintenance Break</h3>

            <div className="admin-form-field" style={{ marginBottom: "16px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", textTransform: "none", fontWeight: "normal", fontSize: "0.875rem", color: "var(--as-text)" }}>
                <input
                  type="checkbox"
                  checked={systemConfig.maintenanceEnabled}
                  onChange={e => setSystemConfig(prev => ({ ...prev, maintenanceEnabled: e.target.checked }))}
                  style={{ width: "16px", height: "16px" }}
                />
                Enable Maintenance Alert
              </label>
            </div>

            {/* Start Date & Time */}
            <div style={{ marginBottom: "16px" }}>
              <label style={{ fontSize: "0.78rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--as-text-2)", marginBottom: "6px", display: "block" }}>Start Date & Time</label>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                <input
                  id="maintenanceStartDate"
                  type="date"
                  className="admin-input"
                  style={{ flex: "1 1 140px", minWidth: "130px" }}
                  value={systemConfig.maintenanceStart ? systemConfig.maintenanceStart.slice(0, 10) : ""}
                  onChange={e => {
                    const datePart = e.target.value;
                    const timePart = systemConfig.maintenanceStart ? systemConfig.maintenanceStart.slice(11) : "12:00";
                    setSystemConfig(prev => ({ ...prev, maintenanceStart: datePart ? `${datePart}T${timePart}` : "" }));
                  }}
                />
                <select
                  className="admin-input"
                  style={{ width: "70px", textAlign: "center" }}
                  value={(() => {
                    if (!systemConfig.maintenanceStart) return "12";
                    const h24 = parseInt(systemConfig.maintenanceStart.slice(11, 13), 10);
                    const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
                    return String(h12);
                  })()}
                  onChange={e => {
                    const h12 = parseInt(e.target.value, 10);
                    const currentH24 = systemConfig.maintenanceStart ? parseInt(systemConfig.maintenanceStart.slice(11, 13), 10) : 0;
                    const isPM = currentH24 >= 12;
                    let newH24 = isPM ? (h12 === 12 ? 12 : h12 + 12) : (h12 === 12 ? 0 : h12);
                    const datePart = systemConfig.maintenanceStart ? systemConfig.maintenanceStart.slice(0, 10) : new Date().toISOString().slice(0, 10);
                    const minPart = systemConfig.maintenanceStart ? systemConfig.maintenanceStart.slice(14, 16) : "00";
                    setSystemConfig(prev => ({ ...prev, maintenanceStart: `${datePart}T${String(newH24).padStart(2, "0")}:${minPart}` }));
                  }}
                >
                  {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(h => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
                <span style={{ color: "var(--as-text-2)", fontWeight: 600 }}>:</span>
                <select
                  className="admin-input"
                  style={{ width: "70px", textAlign: "center" }}
                  value={systemConfig.maintenanceStart ? systemConfig.maintenanceStart.slice(14, 16) : "00"}
                  onChange={e => {
                    const datePart = systemConfig.maintenanceStart ? systemConfig.maintenanceStart.slice(0, 10) : new Date().toISOString().slice(0, 10);
                    const hourPart = systemConfig.maintenanceStart ? systemConfig.maintenanceStart.slice(11, 13) : "00";
                    setSystemConfig(prev => ({ ...prev, maintenanceStart: `${datePart}T${hourPart}:${e.target.value}` }));
                  }}
                >
                  {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0")).map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
                <select
                  className="admin-input"
                  style={{ width: "72px", textAlign: "center", fontWeight: 600 }}
                  value={(() => {
                    if (!systemConfig.maintenanceStart) return "AM";
                    const h24 = parseInt(systemConfig.maintenanceStart.slice(11, 13), 10);
                    return h24 >= 12 ? "PM" : "AM";
                  })()}
                  onChange={e => {
                    const currentH24 = systemConfig.maintenanceStart ? parseInt(systemConfig.maintenanceStart.slice(11, 13), 10) : 0;
                    const currentH12 = currentH24 === 0 ? 12 : currentH24 > 12 ? currentH24 - 12 : currentH24;
                    const isPM = e.target.value === "PM";
                    const newH24 = isPM ? (currentH12 === 12 ? 12 : currentH12 + 12) : (currentH12 === 12 ? 0 : currentH12);
                    const datePart = systemConfig.maintenanceStart ? systemConfig.maintenanceStart.slice(0, 10) : new Date().toISOString().slice(0, 10);
                    const minPart = systemConfig.maintenanceStart ? systemConfig.maintenanceStart.slice(14, 16) : "00";
                    setSystemConfig(prev => ({ ...prev, maintenanceStart: `${datePart}T${String(newH24).padStart(2, "0")}:${minPart}` }));
                  }}
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
              {systemConfig.maintenanceStart && (
                <div style={{ fontSize: "0.75rem", color: "var(--as-text-2)", marginTop: "4px", fontStyle: "italic" }}>
                  {new Date(systemConfig.maintenanceStart).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", hour12: true })}
                </div>
              )}
            </div>

            {/* End Date & Time */}
            <div style={{ marginBottom: "16px" }}>
              <label style={{ fontSize: "0.78rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--as-text-2)", marginBottom: "6px", display: "block" }}>End Date & Time</label>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                <input
                  id="maintenanceEndDate"
                  type="date"
                  className="admin-input"
                  style={{ flex: "1 1 140px", minWidth: "130px" }}
                  value={systemConfig.maintenanceEnd ? systemConfig.maintenanceEnd.slice(0, 10) : ""}
                  onChange={e => {
                    const datePart = e.target.value;
                    const timePart = systemConfig.maintenanceEnd ? systemConfig.maintenanceEnd.slice(11) : "12:00";
                    setSystemConfig(prev => ({ ...prev, maintenanceEnd: datePart ? `${datePart}T${timePart}` : "" }));
                  }}
                />
                <select
                  className="admin-input"
                  style={{ width: "70px", textAlign: "center" }}
                  value={(() => {
                    if (!systemConfig.maintenanceEnd) return "12";
                    const h24 = parseInt(systemConfig.maintenanceEnd.slice(11, 13), 10);
                    const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
                    return String(h12);
                  })()}
                  onChange={e => {
                    const h12 = parseInt(e.target.value, 10);
                    const currentH24 = systemConfig.maintenanceEnd ? parseInt(systemConfig.maintenanceEnd.slice(11, 13), 10) : 0;
                    const isPM = currentH24 >= 12;
                    let newH24 = isPM ? (h12 === 12 ? 12 : h12 + 12) : (h12 === 12 ? 0 : h12);
                    const datePart = systemConfig.maintenanceEnd ? systemConfig.maintenanceEnd.slice(0, 10) : new Date().toISOString().slice(0, 10);
                    const minPart = systemConfig.maintenanceEnd ? systemConfig.maintenanceEnd.slice(14, 16) : "00";
                    setSystemConfig(prev => ({ ...prev, maintenanceEnd: `${datePart}T${String(newH24).padStart(2, "0")}:${minPart}` }));
                  }}
                >
                  {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(h => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
                <span style={{ color: "var(--as-text-2)", fontWeight: 600 }}>:</span>
                <select
                  className="admin-input"
                  style={{ width: "70px", textAlign: "center" }}
                  value={systemConfig.maintenanceEnd ? systemConfig.maintenanceEnd.slice(14, 16) : "00"}
                  onChange={e => {
                    const datePart = systemConfig.maintenanceEnd ? systemConfig.maintenanceEnd.slice(0, 10) : new Date().toISOString().slice(0, 10);
                    const hourPart = systemConfig.maintenanceEnd ? systemConfig.maintenanceEnd.slice(11, 13) : "00";
                    setSystemConfig(prev => ({ ...prev, maintenanceEnd: `${datePart}T${hourPart}:${e.target.value}` }));
                  }}
                >
                  {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0")).map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
                <select
                  className="admin-input"
                  style={{ width: "72px", textAlign: "center", fontWeight: 600 }}
                  value={(() => {
                    if (!systemConfig.maintenanceEnd) return "AM";
                    const h24 = parseInt(systemConfig.maintenanceEnd.slice(11, 13), 10);
                    return h24 >= 12 ? "PM" : "AM";
                  })()}
                  onChange={e => {
                    const currentH24 = systemConfig.maintenanceEnd ? parseInt(systemConfig.maintenanceEnd.slice(11, 13), 10) : 0;
                    const currentH12 = currentH24 === 0 ? 12 : currentH24 > 12 ? currentH24 - 12 : currentH24;
                    const isPM = e.target.value === "PM";
                    const newH24 = isPM ? (currentH12 === 12 ? 12 : currentH12 + 12) : (currentH12 === 12 ? 0 : currentH12);
                    const datePart = systemConfig.maintenanceEnd ? systemConfig.maintenanceEnd.slice(0, 10) : new Date().toISOString().slice(0, 10);
                    const minPart = systemConfig.maintenanceEnd ? systemConfig.maintenanceEnd.slice(14, 16) : "00";
                    setSystemConfig(prev => ({ ...prev, maintenanceEnd: `${datePart}T${String(newH24).padStart(2, "0")}:${minPart}` }));
                  }}
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
              {systemConfig.maintenanceEnd && (
                <div style={{ fontSize: "0.75rem", color: "var(--as-text-2)", marginTop: "4px", fontStyle: "italic" }}>
                  {new Date(systemConfig.maintenanceEnd).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", hour12: true })}
                </div>
              )}
            </div>

            {/* Maintenance Comment */}
            <div className="admin-form-field" style={{ marginBottom: "16px" }}>
              <label htmlFor="maintenanceComment" style={{ fontSize: "0.78rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--as-text-2)", marginBottom: "6px", display: "block" }}>Maintenance Reason / Comment <span style={{ fontWeight: "normal", textTransform: "none", letterSpacing: "0", fontSize: "0.7rem", color: "var(--as-text-2)", opacity: 0.7 }}>(Optional)</span></label>
              <textarea
                id="maintenanceComment"
                className="admin-textarea"
                placeholder="Enter the reason for maintenance (e.g., Server upgrade, Database migration, Security patches...)"
                rows={3}
                value={systemConfig.maintenanceComment}
                onChange={e => setSystemConfig(prev => ({ ...prev, maintenanceComment: e.target.value }))}
              />
              <p style={{ fontSize: "0.75rem", color: "var(--as-text-2)", marginTop: "4px" }}>
                This message will be displayed to users in the maintenance notification to inform them about the reason for the maintenance.
              </p>
            </div>
          </div>

          {/* GCA Browser Download Page Restriction */}
          <div style={{ borderTop: "1px solid var(--as-border)", paddingTop: "20px", marginTop: "20px" }}>
            <h3 style={{ fontSize: "0.95rem", fontWeight: 600, marginBottom: "12px", color: "var(--as-text)" }}>Download Page Access</h3>

            <div className="admin-form-field">
              <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", textTransform: "none", fontWeight: "normal", fontSize: "0.875rem", color: "var(--as-text)" }}>
                <input
                  type="checkbox"
                  checked={systemConfig.restrictDownloadToGcaBrowser}
                  onChange={e => setSystemConfig(prev => ({ ...prev, restrictDownloadToGcaBrowser: e.target.checked }))}
                  style={{ width: "16px", height: "16px" }}
                />
                Restrict /download page to GCA Browser only
              </label>
              <p style={{ fontSize: "0.78rem", color: "var(--as-text-2)", marginTop: "6px", marginLeft: "24px" }}>
                When enabled (default), the full deployment page only appears in GCA Browser — every other browser is shown a "Download GCA Browser" prompt instead.
                Turn this off to let any browser see the full download page.
              </p>
            </div>
          </div>

          {/* Safe Browsing Cache TTLs */}
          <div style={{ borderTop: "1px solid var(--as-border)", paddingTop: "20px", marginTop: "20px" }}>
            <h3 style={{ fontSize: "0.95rem", fontWeight: 600, marginBottom: "12px", color: "var(--as-text)" }}>URL Threat Detection Cache</h3>
            <p style={{ fontSize: "0.78rem", color: "var(--as-text-2)", marginBottom: "12px" }}>
              How long a Google Safe Browsing verdict is trusted before a URL is re-checked live.
            </p>

            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
              <div className="admin-form-field" style={{ flex: "1 1 220px" }}>
                <label htmlFor="safeBrowsingSafeTtlDays" style={{ fontSize: "0.78rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--as-text-2)", marginBottom: "6px", display: "block" }}>
                  Safe Verdict Cache (days)
                </label>
                <input
                  id="safeBrowsingSafeTtlDays"
                  type="number"
                  min="1"
                  className="admin-input"
                  value={systemConfig.safeBrowsingSafeTtlDays}
                  onChange={e => setSystemConfig(prev => ({ ...prev, safeBrowsingSafeTtlDays: e.target.value }))}
                />
              </div>

              <div className="admin-form-field" style={{ flex: "1 1 220px" }}>
                <label htmlFor="safeBrowsingMaliciousTtlDays" style={{ fontSize: "0.78rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--as-text-2)", marginBottom: "6px", display: "block" }}>
                  Malicious Verdict Cache (days)
                </label>
                <input
                  id="safeBrowsingMaliciousTtlDays"
                  type="number"
                  min="1"
                  className="admin-input"
                  value={systemConfig.safeBrowsingMaliciousTtlDays}
                  onChange={e => setSystemConfig(prev => ({ ...prev, safeBrowsingMaliciousTtlDays: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="admin-actions" style={{ marginTop: "12px" }}>
            <button
              type="submit"
              className="admin-btn"
              disabled={systemSaving}
            >
              {systemSaving ? "Saving Settings..." : "Save System Settings"}
            </button>
          </div>
        </form>
      </div>

      {/* ════════════════════════════
          Agent Release Source
          ════════════════════════════ */}
      <AgentReleaseSettings />

      {/* ════════════════════════════
          Blocked Email Domains
          ════════════════════════════ */}
      <div className="admin-panel" style={{ marginTop: "20px" }}>
        <div className="admin-panel-head">
          <div>
            <h2 className="admin-panel-title">Blocked Email Domains</h2>
            <p className="admin-panel-note">
              Registrations from these domains are rejected. Add any domain you want to restrict
              (e.g. <code>gmail.com</code>). Leave the list empty to allow all providers.
            </p>
          </div>
        </div>

        <div className="bd-card">
          <div className="bd-add-row">
            <div className="bd-input-wrap">
              <Globe size={14} className="bd-input-icon" />
              <input
                className="bd-input"
                type="text"
                placeholder="e.g. gmail.com"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleAdd(); }}
                disabled={saving}
              />
            </div>
            <button
              className="admin-btn bd-add-btn"
              onClick={handleAdd}
              disabled={saving || !input.trim()}
            >
              <Plus size={14} />
              {saving ? "Adding…" : "Block Domain"}
            </button>
          </div>

          {loading ? (
            <div className="bd-empty">Loading…</div>
          ) : domains.length === 0 ? (
            <div className="bd-empty">No domains blocked — all email providers are currently allowed.</div>
          ) : (
            <div className="bd-list">
              {domains.map(domain => (
                <div key={domain} className="bd-row">
                  <span className="bd-domain">
                    <Globe size={13} />
                    {domain}
                  </span>
                  <button
                    className="bd-remove-btn"
                    onClick={() => handleRemove(domain)}
                    title={`Remove ${domain}`}
                  >
                    <Trash2 size={14} />
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .bd-card {
          margin-top: 20px;
          background: var(--as-surface, #fff);
          border: 1px solid var(--as-border, #edebe9);
          border-radius: var(--as-r, 4px);
          overflow: hidden;
          max-width: 560px;
          box-shadow: var(--as-shadow);
        }
        .bd-add-row {
          display: flex; align-items: center; gap: 8px;
          padding: 14px 16px;
          border-bottom: 1px solid var(--as-border, #edebe9);
        }
        .bd-input-wrap {
          flex: 1; position: relative; display: flex; align-items: center;
        }
        .bd-input-icon {
          position: absolute; left: 9px; color: var(--as-text-2, #605e5c); pointer-events: none;
        }
        .bd-input {
          width: 100%; height: 32px; padding: 0 8px 0 30px;
          font-size: 13px; font-family: inherit;
          background: var(--as-surface, #fff);
          color: var(--as-text, #323130);
          border: 1px solid var(--as-border-2, #8a8886);
          border-radius: var(--as-r, 4px);
          outline: none; transition: border-color .15s;
        }
        .bd-input:focus { border-color: var(--as-primary, #0078d4); box-shadow: 0 0 0 2px var(--as-primary-bg, #deecf9); }
        .bd-input:disabled { opacity: .55; cursor: not-allowed; }
        .bd-add-btn {
          display: inline-flex; align-items: center; gap: 5px;
          white-space: nowrap; flex-shrink: 0;
        }
        .bd-empty {
          padding: 28px 16px; text-align: center;
          font-size: 13px; color: var(--as-text-2, #605e5c);
        }
        .bd-list { display: flex; flex-direction: column; }
        .bd-row {
          display: flex; align-items: center; justify-content: space-between;
          padding: 10px 16px;
          border-bottom: 1px solid var(--as-surface-2, #f3f2f1);
        }
        .bd-row:last-child { border-bottom: none; }
        .bd-domain {
          display: flex; align-items: center; gap: 8px;
          font-size: 13px; font-family: "Consolas", "Courier New", monospace;
          color: var(--as-text, #323130);
        }
        .bd-remove-btn {
          display: inline-flex; align-items: center; gap: 5px;
          height: 28px; padding: 0 10px;
          font-size: 12px; font-family: inherit;
          background: transparent;
          color: var(--as-danger, #a4262c);
          border: 1px solid transparent; border-radius: var(--as-r, 4px);
          cursor: pointer; transition: background .15s, border-color .15s;
        }
        .bd-remove-btn:hover {
          background: var(--as-danger-bg, rgba(209,52,56,.1));
          border-color: var(--as-danger, #a4262c);
        }
      `}</style>
    </div>
  );
}
